import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { InlineAlert } from "@/components/generic/InlineAlert";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { usePostalCodes } from "@/services/postal-codes.service";
import { comparisonWithBetweenOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircleAlert } from "lucide-react";
import { useMemo } from "react";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z
  .object({
    operator: comparisonWithBetweenOperators.enum,
    values: z.array(z.string()),
    value: z.object({
      from: z.string().nullable(),
      to: z.string().nullable(),
    }),
  })
  .refine(
    ({ operator, value }) =>
      operator === "Between"
        ? value.from && value.to && value.from < value.to
        : true,
    {
      message: "Fra postnummer skal være mindre end til postnummer",
      path: ["value", "from"],
    },
  )
  .refine(
    ({ operator, values }) =>
      operator !== "Between" ? values.length > 0 : true,
    {
      message: "Du skal vælge mindst ét postnummer",
      path: ["values"],
    },
  );

export function PostalCodesSegment({
  queryFilters,
  update,
  formId,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const postalCodesFilter = queryFilters.value;
  const postalCodes = usePostalCodes();

  const { control, handleSubmit, watch, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: useMemo(() => {
      const defaultValues: z.infer<typeof schema> = {
        operator: comparisonWithBetweenOperators.enum
          .catch("Equal")
          .parse(postalCodesFilter?.operator),
        value: { from: null, to: null },
        values: [],
      };

      if (postalCodesFilter?.values) {
        if (defaultValues.operator === "Between") {
          const [from = null, to = null] = postalCodesFilter.values;
          defaultValues.value = { from, to };
        } else {
          defaultValues.values = postalCodesFilter.values;
        }
      }

      return defaultValues;
    }, [postalCodesFilter]),
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const selectedOperator = watch("operator");
  const values = watch("values");

  const submit = handleSubmit((data) => {
    const filter = {
      operator: data.operator,
      values:
        data.operator === "Between"
          ? [data.value.from, data.value.to].filter((value) => value !== null)
          : data.values,
    };

    update({
      value: filter,
    });
  });

  const options = useMemo(
    () =>
      postalCodes.data?.map((c) => ({
        label: c.title,
        value: c.value,
      })) || [],
    [postalCodes.data],
  );

  const optionsMap = useMemo(
    () => new Map(options.map((option) => [option.value.toString(), option])),
    [options],
  );

  const tagFieldPlaceholder = postalCodes.isPending
    ? "Henter postnumre..."
    : "Søg efter postnummer";

  return (
    <div className="flex flex-col gap-y-9">
      <SegmentHeader
        title="Postnummer"
        description="Vælg virksomhedernes postnumre eller et interval af postnumre."
      />

      <Form
        onSubmit={submit}
        className="flex h-full flex-col items-center justify-between gap-y-12"
        id={formId}
      >
        <div className="flex flex-col gap-y-6">
          <div className="mx-auto flex gap-x-4">
            <Controller
              control={control}
              name="operator"
              render={({ field, fieldState: { error } }) => (
                <Select
                  placeholder="Operator"
                  aria-label="Operator"
                  selectedKey={field.value}
                  onSelectionChange={(value) => {
                    field.onChange(value);
                  }}
                  items={Object.entries(
                    comparisonWithBetweenOperators.values,
                  ).map(([key, value]) => ({
                    id: key,
                    label: value,
                  }))}
                  errorMessage={error?.message}
                >
                  {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
              )}
            />

            {postalCodes.isError ? (
              <InlineAlert>
                <div className="flex items-center gap-x-2">
                  <CircleAlert className="size-4 shrink-0 text-red-600" />
                  Postnumre kunne ikke hentes. Prøv igen senere.
                </div>
              </InlineAlert>
            ) : selectedOperator === "Between" ? (
              <div className="flex gap-x-4">
                <Controller
                  control={control}
                  name="value.from"
                  render={({ field, fieldState: { error } }) => (
                    <TagField
                      placeholder={tagFieldPlaceholder}
                      isDisabled={postalCodes.isPending}
                      aria-label="Fra postnummer"
                      selectedOptions={
                        (field.value && optionsMap.get(field.value)) || []
                      }
                      setSelectedOptions={(selectedOptions) => {
                        const selectedOption = Array.isArray(selectedOptions)
                          ? (selectedOptions[0] ?? null)
                          : selectedOptions;
                        const newValue = selectedOption
                          ? selectedOption.value.toString()
                          : null;
                        field.onChange(newValue);
                      }}
                      options={options}
                      filter={(option, query) =>
                        Number.isNaN(Number(query))
                          ? option.label
                              .toLowerCase()
                              .includes(query.toLowerCase())
                          : option.label.startsWith(query)
                      }
                      errorMessage={error?.message}
                      multiple={false}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="value.to"
                  render={({ field, fieldState: { error } }) => (
                    <TagField
                      placeholder={tagFieldPlaceholder}
                      isDisabled={postalCodes.isPending}
                      aria-label="Til postnummer"
                      selectedOptions={
                        (field.value && optionsMap.get(field.value)) || []
                      }
                      setSelectedOptions={(selectedOptions) => {
                        const selectedOption = Array.isArray(selectedOptions)
                          ? (selectedOptions[0] ?? null)
                          : selectedOptions;
                        const newValue = selectedOption
                          ? selectedOption.value.toString()
                          : null;
                        field.onChange(newValue);
                      }}
                      filter={(option, query) =>
                        Number.isNaN(Number(query))
                          ? option.label
                              .toLowerCase()
                              .includes(query.toLowerCase())
                          : option.label.startsWith(query)
                      }
                      options={options}
                      errorMessage={error?.message}
                      multiple={false}
                    />
                  )}
                />
              </div>
            ) : (
              <Controller
                control={control}
                name="values"
                render={({ field, fieldState: { error } }) => (
                  <TagField
                    placeholder={tagFieldPlaceholder}
                    isDisabled={postalCodes.isPending}
                    aria-label="Postnummer"
                    selectedOptions={field.value
                      .map((value) => optionsMap.get(value))
                      .filter((o) => o !== undefined)}
                    setSelectedOptions={(values) => {
                      if (Array.isArray(values))
                        return field.onChange(
                          values.map((item) => item.value.toString()),
                        );
                    }}
                    filter={(option, query) =>
                      Number.isNaN(Number(query))
                        ? option.label
                            .toLowerCase()
                            .includes(query.toLowerCase())
                        : option.label.startsWith(query)
                    }
                    options={options}
                    errorMessage={error?.message}
                    multiple={true}
                  />
                )}
              />
            )}
          </div>

          {selectedOperator !== "Between" && (
            <Controller
              control={control}
              name="values"
              render={({ field }) => (
                <ul className="flex flex-col gap-y-2">
                  {values.map((value, index) => (
                    <ListItem
                      key={value}
                      label={`${
                        selectedOperator === "Equal"
                          ? "Lig med"
                          : "Ikke lig med"
                      } ${optionsMap.get(value)?.label ?? value}`}
                      onRemove={() => {
                        const newValues = [...values];
                        newValues.splice(index, 1);
                        field.onChange(newValues);
                      }}
                    />
                  ))}
                </ul>
              )}
            />
          )}
        </div>
      </Form>
    </div>
  );
}
